import type { FormApi, ReactFormApi } from '@tanstack/react-form';
import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { validators } from '@connected/api-client/generated/validators.generated';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import { SingleHorizontalOptionItem, SingleOptionField } from '@connected/form-fields/components/option';
import { Button } from '@connected/ui/components/button';
import { FileInput } from '@connected/ui/components/file';
import { Input } from '@connected/ui/components/input';
import { Textarea } from '@connected/ui/components/textarea';
import { Loader2, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ElectricalProjectImage } from '@/components/electrical-project-image';
import { useElectricalProjectFileDelete } from '@/hooks/use-electrical-project-file-delete';
import { useElectricalProjectFileUpload } from '@/hooks/use-electrical-project-file-upload';

import type { TechnicalDrawingFormFields } from '../utils/drawing-types';

type DrawingFormFieldsProps = {
    form: FormApi<TechnicalDrawingFormFields, ZodValidator> & ReactFormApi<TechnicalDrawingFormFields, ZodValidator>;
    disabled: boolean;
    electricalProjectVersionId: string;
};

export function DrawingFormFields({
    form,
    disabled,
    electricalProjectVersionId,
}: DrawingFormFieldsProps) {
    const { t } = useTranslation('applications');

    const fileUpload = useElectricalProjectFileUpload({
        endpoint: '/api/v2/electrical/projects/files/upload/technicaldrawing',
        mutationOptions: {
            onMutate: async (file) => {
                form.setFieldValue('ElectricalProjectFileId', file.ElectricalProjectFileId);
            },
            scope: {
                id: electricalProjectVersionId,
            },
        },
    });

    const fileDelete = useElectricalProjectFileDelete({
        mutationOptions: {
            onMutate: async () => {
                form.setFieldValue('ElectricalProjectFileId', null);
            },
        },
    });

    return (
        <>
            <form.Field
                name="IsDrawingPresent"
                validators={{
                    onChange: validators.createElectricalProjectTechnicalDrawingCommand.isDrawingPresent,
                }}
                children={field => (
                    <Label label="Is de tekening aanwezig?" htmlFor={field.name}>
                        <SingleOptionField
                            className="flex-row"
                            name={field.name}
                            state={field.state}
                            handleChange={value => field.handleChange(value)}
                            options={[
                                { label: 'Ja', value: true },
                                { label: 'Nee', value: false },
                            ]}
                            disabled={disabled}
                            render={SingleHorizontalOptionItem}
                        />
                        <FieldInfo state={field.state} />
                    </Label>
                )}
            />

            <form.Subscribe
                selector={state => state.values.IsDrawingPresent}
                children={isDrawingPresent => isDrawingPresent && (
                    <form.Subscribe
                        selector={state => state.values.ElectricalProjectFileId}
                        children={ElectricalProjectFileId => !ElectricalProjectFileId
                            ? (
                                    <FileInput
                                        accept={fileUpload.accept ?? undefined}
                                        onChange={(files) => {
                                            const file = files[0];

                                            if (!file)
                                                return;

                                            fileUpload.upload({ file });
                                        }}
                                        mode="both"
                                    />
                                )
                            : (
                                    <div className="relative flex flex-col gap-2">
                                        <ElectricalProjectImage
                                            className="h-[112px] w-full rounded-lg border border-input-border"
                                            query={{
                                                ElectricalProjectFileId,
                                            }}
                                            alt={t('drawings.item.image.alt')}
                                            expandable
                                        />
                                        <Button
                                            className="absolute top-1 right-1 bg-background/50 hover:bg-background backdrop-blur-sm"
                                            variant="outline"
                                            size="icon"
                                            onClick={() => {
                                                form.setFieldValue('ElectricalProjectFileId', null);
                                                fileDelete.delete(ElectricalProjectFileId);
                                            }}
                                            disabled={fileDelete.isDisabled}
                                        >
                                            {fileDelete.isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 />}
                                        </Button>
                                    </div>
                                )}
                    />
                )}
            />

            <form.Field
                name="Name"
                validators={{
                    onChange: validators.createElectricalProjectTechnicalDrawingCommand.name,
                }}
                children={field => (
                    <Label>
                        <span>Naam/nummer</span>
                        <Input
                            placeholder="Noteer naam of nummer"
                            onChange={event => field.handleChange(event.target.value)}
                            onBlur={field.handleBlur}
                            value={field.state.value ?? ''}
                        />
                        <FieldInfo state={field.state} />
                    </Label>
                )}
            />

            <form.Field
                name="Location"
                validators={{
                    onChange: validators.createElectricalProjectTechnicalDrawingCommand.location,
                }}
                children={field => (
                    <Label>
                        <span>Waar te vinden</span>
                        <Input
                            placeholder="Noteer locatie"
                            onChange={event => field.handleChange(event.target.value)}
                            onBlur={field.handleBlur}
                            value={field.state.value ?? ''}
                        />
                        <FieldInfo state={field.state} />
                    </Label>
                )}
            />

            <form.Field
                name="Description"
                validators={{
                    onChange: validators.createElectricalProjectTechnicalDrawingCommand.description,
                }}
                children={field => (
                    <Label>
                        <span>Omschrijving</span>
                        <Textarea
                            placeholder="Noteer omschrijving"
                            rows={5}
                            onChange={event => field.handleChange(event.target.value)}
                            onBlur={field.handleBlur}
                            value={field.state.value ?? ''}
                        />
                        <FieldInfo state={field.state} />
                    </Label>
                )}
            />
        </>
    );
}
